import React, { useState, useCallback } from 'react';
import './CalendarioGlobal.css';
import { apiFetch, ApiError } from './api';
import { useRecurso } from './hooks/useRecurso';
import EstadoCarregando from './EstadoCarregando';
import EstadoErro from './EstadoErro';

/**
 * Calendário global do escritório (PRD 16 / SEF-28) — visão "agenda por dia" de uma janela
 * de 7 dias, consumindo GET /api/agendamentos/calendario. 1ª fatia: lista por dia com
 * navegação de período e filtro "meus" (grade mensal fica como evolução).
 */

const DIAS_JANELA = 7;

const ROTULOS_TIPO = {
  reuniao: 'Reunião',
  ligacao: 'Ligação',
  email: 'E-mail',
  visita: 'Visita',
  outro: 'Outro',
};

const MENSAGENS_ERRO = {
  FORBIDDEN: 'Você não tem permissão para ver a agenda.',
  RATE_LIMITED: 'Muitas requisições. Aguarde um momento e tente novamente.',
};

function hojeISO() {
  return new Date().toISOString().slice(0, 10);
}

function somarDiasISO(iso, n) {
  const d = new Date(`${iso}T00:00:00`);
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

function formatarDiaLongo(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return d.toLocaleDateString('pt-BR', { weekday: 'long', day: '2-digit', month: 'short' });
}

const DIAS_GRADE = 42; // 6 semanas x 7 dias
const NOMES_DIA_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

function primeiroDoMesISO(iso) {
  return `${iso.slice(0, 7)}-01`;
}

// Domingo no/antes do 1º do mês — início da grade mensal.
function inicioGradeMes(mesIso) {
  const primeiro = primeiroDoMesISO(mesIso);
  const diaSemana = new Date(`${primeiro}T00:00:00`).getDay(); // 0=domingo
  return somarDiasISO(primeiro, -diaSemana);
}

function navegarMes(mesIso, delta) {
  const d = new Date(`${primeiroDoMesISO(mesIso)}T00:00:00`);
  d.setMonth(d.getMonth() + delta);
  return d.toISOString().slice(0, 10);
}

function formatarMesAno(iso) {
  return new Date(`${primeiroDoMesISO(iso)}T00:00:00`)
    .toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' });
}

// Token sem acento para a classe CSS do badge (status vem como label do backend).
function tokenStatus(label) {
  return String(label).toLowerCase().normalize('NFD').replace(/\p{M}+/gu, '');
}

function mensagemErro(erro) {
  if (erro instanceof ApiError) {
    if (erro.status === 429) return MENSAGENS_ERRO.RATE_LIMITED;
    if (erro.code && MENSAGENS_ERRO[erro.code]) return MENSAGENS_ERRO[erro.code];
    return 'Não foi possível carregar a agenda.';
  }
  return 'Erro de conexão. Verifique sua internet e tente novamente.';
}

const TIPOS = Object.keys(ROTULOS_TIPO);

function formInicial(dataPadrao) {
  return { clienteId: '', data: dataPadrao, hora: '', tipo: 'reuniao', assunto: '', descricao: '' };
}

function mensagemErroForm(erro) {
  if (erro instanceof ApiError) {
    if (erro.status === 404) return 'Cliente não encontrado.';
    if (erro.status === 400) return 'Verifique os campos do formulário.';
    if (erro.code && MENSAGENS_ERRO[erro.code]) return MENSAGENS_ERRO[erro.code];
  }
  return 'Não foi possível salvar. Tente novamente.';
}

function CalendarioGlobal({ onNavegar }) {
  const [visao, setVisao] = useState('semana');
  const [inicio, setInicio] = useState(hojeISO());
  const [mesRef, setMesRef] = useState(() => primeiroDoMesISO(hojeISO()));
  const [meus, setMeus] = useState(false);

  // Range único: semana = janela de 7 dias; mês = grade de 42 dias (inclui dias externos).
  const fim = somarDiasISO(inicio, DIAS_JANELA - 1);
  const gradeInicio = inicioGradeMes(mesRef);
  const gradeFim = somarDiasISO(gradeInicio, DIAS_GRADE - 1);
  const dataInicio = visao === 'mes' ? gradeInicio : inicio;
  const dataFim = visao === 'mes' ? gradeFim : fim;

  const buscar = useCallback(() => {
    const params = new URLSearchParams();
    params.set('dataInicio', dataInicio);
    params.set('dataFim', dataFim);
    if (meus) params.set('meus', 'true');
    return apiFetch(`/api/agendamentos/calendario?${params.toString()}`);
  }, [dataInicio, dataFim, meus]);

  const { dados, carregando, erro, recarregar } = useRecurso(buscar, [dataInicio, dataFim, meus]);
  const dias = dados?.dias ?? [];

  const irAnterior = () => setInicio((i) => somarDiasISO(i, -DIAS_JANELA));
  const irProxima = () => setInicio((i) => somarDiasISO(i, DIAS_JANELA));
  const irHoje = () => {
    setMesRef(primeiroDoMesISO(hojeISO()));
    setInicio(hojeISO());
  };
  const irMesAnterior = () => setMesRef((m) => navegarMes(m, -1));
  const irMesProximo = () => setMesRef((m) => navegarMes(m, 1));
  const abrirDia = (diaIso) => {
    setInicio(diaIso);
    setVisao('semana');
  };
  // Ao entrar no Mês, a grade acompanha a semana visível (review CTO #47).
  const irParaMes = () => {
    setMesRef(primeiroDoMesISO(inicio));
    setVisao('mes');
  };
  // Data padrão do form contextual à visão: no Mês, hoje se for o mês atual, senão o 1º exibido.
  const dataPadraoForm = () => {
    if (visao !== 'mes') return inicio;
    return mesRef.slice(0, 7) === hojeISO().slice(0, 7) ? hojeISO() : mesRef;
  };

  const [formAberto, setFormAberto] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [form, setForm] = useState(() => formInicial(hojeISO()));
  const [salvando, setSalvando] = useState(false);
  const [erroForm, setErroForm] = useState(null);

  const abrirForm = () => {
    setForm(formInicial(dataPadraoForm()));
    setErroForm(null);
    setFormAberto(true);
    apiFetch('/api/clientes?perPage=100&orderBy=nome')
      .then((d) => setClientes(d?.data ?? []))
      .catch(() => setClientes([]));
  };
  const fecharForm = () => setFormAberto(false);
  const mudar = (campo) => (e) => setForm((f) => ({ ...f, [campo]: e.target.value }));

  const salvar = (e) => {
    e.preventDefault();
    if (!form.clienteId || !form.data || !form.hora || !form.assunto.trim()) {
      setErroForm('Preencha cliente, data, hora e assunto.');
      return;
    }
    setSalvando(true);
    setErroForm(null);
    apiFetch('/api/agendamentos', {
      method: 'POST',
      body: {
        clienteId: form.clienteId,
        data: form.data,
        hora: form.hora,
        tipo: form.tipo,
        assunto: form.assunto.trim(),
        descricao: form.descricao.trim() || null,
      },
    })
      .then(() => {
        setSalvando(false);
        setFormAberto(false);
        recarregar();
      })
      .catch((err) => {
        setSalvando(false);
        setErroForm(mensagemErroForm(err));
      });
  };

  return (
    <div className="calendario-container">
      <div className="calendario-header">
        <h1>Calendário</h1>
        <p className="descricao">Agendamentos do escritório, agrupados por dia</p>
      </div>

      <div className="calendario-controles">
        <div className="calendario-toggle" role="group" aria-label="Visão do calendário">
          <button
            type="button"
            className={`calendario-toggle-btn${visao === 'semana' ? ' calendario-toggle-btn--ativo' : ''}`}
            aria-pressed={visao === 'semana'}
            onClick={() => setVisao('semana')}
          >Semana</button>
          <button
            type="button"
            className={`calendario-toggle-btn${visao === 'mes' ? ' calendario-toggle-btn--ativo' : ''}`}
            aria-pressed={visao === 'mes'}
            onClick={irParaMes}
          >Mês</button>
        </div>
        <div className="calendario-periodo">
          {visao === 'mes' ? (
            <>
              <button type="button" className="calendario-nav" onClick={irMesAnterior} aria-label="Mês anterior">
                <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
              </button>
              <span className="calendario-periodo-label calendario-periodo-label--mes">
                {formatarMesAno(mesRef)}
              </span>
              <button type="button" className="calendario-nav" onClick={irMesProximo} aria-label="Próximo mês">
                <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
              </button>
            </>
          ) : (
            <>
              <button type="button" className="calendario-nav" onClick={irAnterior} aria-label="Semana anterior">
                <i className="fa-solid fa-chevron-left" aria-hidden="true"></i>
              </button>
              <span className="calendario-periodo-label">
                {formatarDiaLongo(inicio)} – {formatarDiaLongo(fim)}
              </span>
              <button type="button" className="calendario-nav" onClick={irProxima} aria-label="Próxima semana">
                <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
              </button>
            </>
          )}
          <button type="button" className="calendario-hoje" onClick={irHoje}>Hoje</button>
        </div>
        <label className="calendario-meus">
          <input type="checkbox" checked={meus} onChange={(e) => setMeus(e.target.checked)} />
          Só os meus
        </label>
        <button type="button" className="btn-primary calendario-novo" onClick={abrirForm}>
          <i className="fa-solid fa-plus" aria-hidden="true"></i> Novo agendamento
        </button>
      </div>

      {carregando ? (
        <EstadoCarregando mensagem="Carregando agenda..." />
      ) : erro ? (
        <EstadoErro mensagem={mensagemErro(erro)} onRetry={recarregar} />
      ) : visao === 'mes' ? (
        <div className="calendario-mes">
          <div className="calendario-mes-cabecalho">
            {NOMES_DIA_SEMANA.map((d) => (
              <span key={d} className="calendario-mes-dow">{d}</span>
            ))}
          </div>
          <div className="calendario-mes-grade">
            {(() => {
              const porData = new Map(dias.map((d) => [d.data, d]));
              const hoje = hojeISO();
              const mesAtual = mesRef.slice(0, 7);
              return Array.from({ length: DIAS_GRADE }, (_, i) => {
                const diaIso = somarDiasISO(gradeInicio, i);
                const total = porData.get(diaIso)?.total ?? 0; // contador vem de dia.total
                const classes = ['calendario-cel'];
                if (diaIso.slice(0, 7) !== mesAtual) classes.push('calendario-cel--outro-mes');
                if (diaIso === hoje) classes.push('calendario-cel--hoje');
                return (
                  <button
                    key={diaIso}
                    type="button"
                    className={classes.join(' ')}
                    onClick={() => abrirDia(diaIso)}
                    aria-label={`${formatarDiaLongo(diaIso)}, ${total} agendamento(s)`}
                  >
                    <span className="calendario-cel-dia">{Number(diaIso.slice(8, 10))}</span>
                    {total > 0 && <span className="calendario-cel-total">{total}</span>}
                  </button>
                );
              });
            })()}
          </div>
        </div>
      ) : (
        <div className="calendario-dias">
          {dias.map((dia) => (
            <section key={dia.data} className="calendario-dia">
              <h2 className="calendario-dia-titulo">
                {formatarDiaLongo(dia.data)}
                <span className="calendario-dia-total">{dia.total}</span>
              </h2>
              {dia.agendamentos.length === 0 ? (
                <p className="calendario-dia-vazio">Sem agendamentos.</p>
              ) : (
                <ul className="calendario-lista">
                  {dia.agendamentos.map((ag) => (
                    <li key={ag.id} className="calendario-item">
                      <span className="calendario-item-hora">{String(ag.hora).slice(0, 5)}</span>
                      <button
                        type="button"
                        className="calendario-item-corpo"
                        onClick={() => onNavegar('detalhes-cliente', { clienteId: ag.clienteId })}
                      >
                        <span className="calendario-item-assunto">{ag.assunto}</span>
                        <span className="calendario-item-meta">
                          {(ROTULOS_TIPO[ag.tipo] || ag.tipo)} · {ag.clienteNome}
                        </span>
                      </button>
                      <span className={`calendario-badge calendario-badge--${tokenStatus(ag.status)}`}>
                        {ag.status}
                      </span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>
      )}

      {formAberto && (
        <div className="calendario-modal-overlay" onClick={fecharForm}>
          <div
            className="calendario-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="calendario-modal-titulo"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="calendario-modal__cabecalho">
              <h2 id="calendario-modal-titulo">Novo agendamento</h2>
              <button
                type="button"
                className="calendario-modal__fechar"
                aria-label="Fechar"
                onClick={fecharForm}
              >×</button>
            </div>
            <form className="calendario-modal__corpo" onSubmit={salvar} noValidate>
              <div className="form-group">
                <label htmlFor="cal-cliente">Cliente</label>
                <select id="cal-cliente" value={form.clienteId} onChange={mudar('clienteId')}>
                  <option value="">Selecione...</option>
                  {clientes.map((c) => (
                    <option key={c.id} value={c.id}>{c.nome}</option>
                  ))}
                </select>
              </div>
              <div className="calendario-modal__linha">
                <div className="form-group">
                  <label htmlFor="cal-data">Data</label>
                  <input id="cal-data" type="date" value={form.data} onChange={mudar('data')} />
                </div>
                <div className="form-group">
                  <label htmlFor="cal-hora">Hora</label>
                  <input id="cal-hora" type="time" value={form.hora} onChange={mudar('hora')} />
                </div>
                <div className="form-group">
                  <label htmlFor="cal-tipo">Tipo</label>
                  <select id="cal-tipo" value={form.tipo} onChange={mudar('tipo')}>
                    {TIPOS.map((t) => (
                      <option key={t} value={t}>{ROTULOS_TIPO[t]}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label htmlFor="cal-assunto">Assunto</label>
                <input
                  id="cal-assunto"
                  type="text"
                  maxLength={200}
                  value={form.assunto}
                  onChange={mudar('assunto')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="cal-descricao">Descrição</label>
                <textarea
                  id="cal-descricao"
                  maxLength={2000}
                  value={form.descricao}
                  onChange={mudar('descricao')}
                />
              </div>
              <div className="form-group">
                <label htmlFor="cal-responsavel">Responsável</label>
                <input id="cal-responsavel" type="text" value="Você (usuário logado)" readOnly />
              </div>
              {erroForm && <p className="calendario-modal__erro" role="alert">{erroForm}</p>}
              <div className="calendario-modal__acoes">
                <button type="button" className="calendario-modal__cancelar" onClick={fecharForm}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary" disabled={salvando}>
                  {salvando ? 'Salvando...' : 'Criar agendamento'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default CalendarioGlobal;
